(() => {
  const INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];
  const HULK_STYLE_URL = 'https://cdn.shopify.com/extensions/019a0a1f-8909-70ad-9758-898ea7374bfc/productoption-145/assets/hulkcode.css';
  const scriptRegistry = new Map();
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  let primaryJqueryResolver = null;
  let primaryJquery = null;
  const primaryJqueryReady = new Promise((resolve) => {
    primaryJqueryResolver = resolve;
  });

  function settlePrimaryJquery(jqInstance) {
    if (!jqInstance || typeof jqInstance.fn !== 'object') return;
    primaryJquery = jqInstance;
    if (jqInstance.noConflict && !jqInstance.__patchedNoConflict) {
      const originalNoConflict = jqInstance.noConflict.bind(jqInstance);
      jqInstance.noConflict = (removeAll) => {
        const result = originalNoConflict(removeAll);
        // Restore globals immediately so downstream scripts still see jQuery.
        if (removeAll) {
          window.jQuery = primaryJquery;
          window.$ = primaryJquery;
        }
        return result;
      };
      jqInstance.__patchedNoConflict = true;
    }
    window.__primaryJquery = jqInstance;
    if (!window.jQuery) window.jQuery = jqInstance;
    if (!window.$) window.$ = jqInstance;
    if (typeof primaryJqueryResolver === 'function') {
      primaryJqueryResolver(jqInstance);
      primaryJqueryResolver = null;
    }
  }

  function withPrimaryJquery(callback) {
    if (!primaryJquery && scriptRegistry.has('jquery-core')) {
      activateScript('jquery-core');
    }
    return primaryJqueryReady.then((jqInstance) => {
      try {
        callback(jqInstance);
      } catch (error) {
        console.error('Deferred integrations: jQuery callback failed', error);
      }
      return jqInstance;
    });
  }

  if (window.jQuery && typeof window.jQuery.fn === 'object') {
    settlePrimaryJquery(window.jQuery);
  }
  window.__primaryJqueryReady = primaryJqueryReady;

  document.querySelectorAll('script[data-deferred-script]').forEach((node) => {
    const name = node.dataset.deferredScript;
    if (!name || scriptRegistry.has(name)) return;
    scriptRegistry.set(name, node);
  });

  function activateScript(name) {
    const node = scriptRegistry.get(name);
    if (!node || node.dataset.deferredLoaded === 'true') return null;

    const script = document.createElement('script');
    Array.from(node.attributes).forEach((attr) => {
      if (attr.name === 'type' || attr.name === 'data-deferred-script') return;
      script.setAttribute(attr.name, attr.value);
    });

    const srcAttr = node.getAttribute('src');
    if (srcAttr) {
      script.src = srcAttr;
    } else if (node.textContent) {
      script.textContent = node.textContent;
    }

    if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
    node.dataset.deferredLoaded = 'true';
    scriptRegistry.delete(name);
    script.dataset.deferredLoaded = 'true';
    script.dataset.lazyLoaded = 'true';
    if (name === 'jquery-core') {
      script.addEventListener('load', () => {
        settlePrimaryJquery(window.jQuery || window.$ || primaryJquery);
      }, { once: true });
      script.addEventListener('error', () => {
        console.warn('Deferred integrations: unable to load primary jQuery.');
      }, { once: true });
    }
    document.head.appendChild(script);
    return script;
  }

  function isMobileViewport() {
    if (window.matchMedia) {
      return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    }
    return window.innerWidth <= 749;
  }

  function loadStyleOnce(href, key) {
    if (!href) return null;
    const selector = key
      ? `link[data-style-key="${key}"]`
      : `link[href="${href}"]`;
    let link = document.head.querySelector(selector);
    if (link) return link;

    link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    if (key) {
      link.dataset.styleKey = key;
    }
    document.head.appendChild(link);
    return link;
  }

  function observeElementOnce(element, callback, rootMargin = '0px 0px 200px 0px') {
    if (!element) return;
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }

    let fired = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (fired || !entry.isIntersecting) return;
        fired = true;
        observer.disconnect();
        callback();
      });
    }, { rootMargin });

    observer.observe(element);
  }

  function observeOnce(element, callback, rootMargin = '0px') {
    if (!element) return;
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    let fired = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (fired || !entry.isIntersecting) return;
        fired = true;
        observer.disconnect();
        callback();
      });
    }, { rootMargin });
    observer.observe(element);
  }

  function scheduleWpm() {
    const hasRegistryScript = scriptRegistry.has('wpm');
    const hasQueuedScript = Array.isArray(window.__deferredScriptsQueue)
      ? window.__deferredScriptsQueue.some((item) => item && item.name === 'wpm')
      : false;

    if (!hasRegistryScript && !hasQueuedScript) return;

    let scheduled = false;
    let timer = null;

    const trigger = (delay) => {
      if (scheduled) return;
      scheduled = true;
      timer = window.setTimeout(() => {
        if (hasRegistryScript) {
          activateScript('wpm');
        } else if (typeof window.__runDeferredScripts === 'function') {
          window.__runDeferredScripts('wpm');
        }
      }, delay);
    };

    INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, () => trigger(2000), { once: true, passive: true });
    });

    window.addEventListener('scroll', () => trigger(4000), { once: true, passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') trigger(0);
    }, { once: true });

    window.setTimeout(() => trigger(20000), 20000);
  }

  function scheduleDeferredScript(name, delay = 2000) {
    if (!scriptRegistry.has(name)) return;
    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      activateScript(name);
    };

    INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, () => trigger(), { once: true, passive: true });
    });

    if (delay) {
      window.setTimeout(trigger, delay);
    }
  }

  function scheduleHulk() {
    const container = document.querySelector('[data-hulkapps-product-options]');
    if (!container) return;

    const buttonTrigger = container.querySelector('[data-hulk-trigger]');
    const manualMode = !!buttonTrigger;

    const ensureConfig = () => activateScript('hulk-config');
    const ensureRelocate = () => activateScript('hulk-relocate');
    const ensureExternal = () => {
      const script = activateScript('hulk-external');
      if (script) {
        loadStyleOnce(HULK_STYLE_URL, 'hulkapps');
      }
    };

    let triggered = false;
    const runActivation = () => {
      ensureConfig();
      ensureRelocate();
      window.setTimeout(ensureExternal, 10);
    };
    const dispatchError = () => {
      const errorEvent = new CustomEvent('hulkapps:loading-error', { bubbles: true });
      container.dispatchEvent(errorEvent);
    };

    const trigger = () => {
      if (triggered) return;
      triggered = true;
      container.dispatchEvent(new CustomEvent('hulkapps:loading-started', { bubbles: true }));
      withPrimaryJquery((jq) => {
        if (!window.hulkapps_jQuery) {
          window.hulkapps_jQuery = jq;
        }
        if (window.hulkapps && typeof window.hulkapps === 'object') {
          window.hulkapps.jQuery = window.hulkapps.jQuery || jq;
          window.hulkapps.$ = window.hulkapps.$ || jq;
        }
      }).then(runActivation).catch(() => {
        try {
          runActivation();
        } catch (error) {
          triggered = false;
          dispatchError();
          throw error;
        }
      }).catch(() => {
        triggered = false;
        dispatchError();
      });
    };

    if (manualMode && !(window.Shopify && window.Shopify.designMode)) {
      const manualHandler = () => trigger();
      container.addEventListener('hulkapps:manual-request', manualHandler);
      window.addEventListener('hulkapps:manual-request', manualHandler);
      return;
    }

    observeElementOnce(container, trigger, '0px 0px 200px 0px');
    const eventOptions = { once: true, passive: true };
    INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, trigger, eventOptions);
    });
    window.addEventListener('scroll', trigger, eventOptions);
    container.addEventListener('pointerdown', trigger, { once: true, passive: true });
    container.addEventListener('pointerover', trigger, { once: true, passive: true });
    container.addEventListener('focusin', trigger, { once: true });
    container.addEventListener('click', trigger, { once: true });
    container.addEventListener('change', trigger, { once: true });

    window.addEventListener('load', () => {
      window.setTimeout(trigger, 60000);
    }, { once: true });
  }

  function scheduleShopperApproved() {
    if (!scriptRegistry.has('shopperapproved')) return;

    if (isMobileViewport()) {
      scriptRegistry.delete('shopperapproved');
      return;
    }

    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      activateScript('shopperapproved');
    };

    const containers = document.querySelectorAll('[data-lazy-integration="shopper"]');
    containers.forEach((container) => observeElementOnce(container, trigger, '0px 0px 200px 0px'));

    window.addEventListener('load', () => {
      window.setTimeout(trigger, 45000);
    }, { once: true });
  }

  function scheduleStampedWidget() {
    if (!scriptRegistry.has('stamped-widget')) return;

    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      withPrimaryJquery((jq) => {
        window.jQueryStamped = window.jQueryStamped || jq;
        if (!window.StampedGlobalOptions) window.StampedGlobalOptions = {};
        window.StampedGlobalOptions.is_force_jquery = false;
        window.StampedGlobalOptions.exclude_jquery = true;
      }).catch(() => {
        if (!window.StampedGlobalOptions) window.StampedGlobalOptions = {};
        window.StampedGlobalOptions.is_force_jquery = false;
      }).then(() => {
        activateScript('stamped-widget');
      });
    };

    if (window.Shopify && window.Shopify.designMode) {
      trigger();
      return;
    }

    window.addEventListener('lazy:stamped:request', trigger, { once: true });
  }

  scheduleWpm();
  scheduleDeferredScript('jquery-core', 0);
  scheduleDeferredScript('facebook-pixel', 2000);
  scheduleStampedWidget();
  scheduleShopperApproved();
  scheduleHulk();
})();
